import { Entity } from "breeze-client";
import { EntityStateRendererComponent } from "./entity-state-renderer.component";
import { AgButtonProps } from "./button-renderer.component";
import { AgFns } from "./ag-fns";
import { PoHeader } from "@/model/entities/po-header";
import { PoDetail } from "@/model/entities/po-detail";
import { JoHeader } from "@/model/entities/jo-header";

export class AgStyleFns {
  // CSS class names defined in ag-grid-quickbooks.scss
  private static readonly EDITABLE_CLASS = "cell-editable";
  private static readonly LINK_CLASS = "cell-link";

  /**
   * Returns the CSS class for an editable cell.
   * Use with cellClass property in column definitions.
   */
  static getEditableCellClass(): string {
    return AgStyleFns.EDITABLE_CLASS;
  }

  /**
   * Returns the CSS class for a clickable link cell.
   * Use with cellClass property in column definitions.
   * Provides theme-aware link color that works in both light and dark modes.
   */
  static getLinkCellClass(): string {
    return AgStyleFns.LINK_CLASS;
  }

  /**
   * @deprecated Use getEditableCellClass with cellClass instead of cellStyle
   * Returns inline styles for backward compatibility
   */
  static setEditableStyle(p: any) {
    return { backgroundColor: "var(--p-primary-50, #f0f7ff)", color: "#1a1a1a" };
  }

  static getEntityStateColDef(getEntityFn?: (item: any) => Entity) {
    const cellRendererParams: any = {};
    if (getEntityFn) {
      cellRendererParams.getEntityFn = getEntityFn;
    }
    return {
      headerName: "State",
      sortable: false,
      cellRenderer: EntityStateRendererComponent,
      cellRendererParams: cellRendererParams,
      minWidth: 75,
      maxWidth: 75,
    };
  }

  static getEntityDeleteColDef<T>(
    deleteMethodBound: (arg: T) => void | Promise<void> | Promise<boolean>,
    buttonProps?: AgButtonProps
  ) {
    return {
      ...AgFns.createButtonProps("", deleteMethodBound, {
        icon: "pi pi-trash",
        ...buttonProps,
      }),
      minWidth: 40,
      maxWidth: 60,
    };
  }

  // CSS class names defined in ag-grid-quickbooks.scss
  private static readonly PAST_DUE_CLASS = "cell-past-due";
  private static readonly ON_TIME_CLASS = "cell-on-time";
  private static readonly DATE_EDIT_PAST_DUE_CLASS = "cell-date-edit-past-due";
  private static readonly DATE_EDIT_ON_TIME_CLASS = "cell-date-edit-on-time";

  /**
   * Returns the CSS class for a date cell based on whether the date is past due.
   * Use with cellClass property in column definitions.
   */
  static getDueDateCellClass(d: Date): string {
    if (new Date(d) < new Date()) {
      return AgStyleFns.PAST_DUE_CLASS;
    } else {
      return AgStyleFns.ON_TIME_CLASS;
    }
  }

  /**
   * Returns the CSS class for an editable date cell.
   * Uses yellow for on-time (editable emphasis) and red for past-due.
   */
  static getEditableDueDateCellClass(d: Date): string {
    if (new Date(d) < new Date()) {
      return AgStyleFns.DATE_EDIT_PAST_DUE_CLASS;
    } else {
      return AgStyleFns.DATE_EDIT_ON_TIME_CLASS;
    }
  }

  /**
   * Returns the CSS class for a PO detail date cell.
   * Only applies styling if the PO is processed and has remaining quantity.
   */
  static getPodDueDateCellClass(
    isProcessed: boolean,
    isRemainingQty: boolean,
    d: Date
  ): string {
    if (isProcessed && isRemainingQty) {
      return AgStyleFns.getDueDateCellClass(d);
    }
    return "";
  }

  /**
   * @deprecated Use getDueDateCellClass with cellClass instead of cellStyle
   * Returns inline styles for backward compatibility
   */
  static getDueDateCellStyle(d: Date) {
    if (new Date(d) < new Date()) {
      return { "background-color": "#ffcccc", "color": "#1a1a1a" };
    } else {
      return { "background-color": "#90ee90", "color": "#1a1a1a" };
    }
  }

  /**
   * @deprecated Use getPodDueDateCellClass with cellClass instead of cellStyle
   * Returns inline styles for backward compatibility
   */
  static getPodDueDateCellStyle(
    isProcessed: boolean,
    isRemainingQty: boolean,
    d: Date
  ) {
    if (isProcessed && isRemainingQty) {
      return AgStyleFns.getDueDateCellStyle(d);
    }
    return null;
  }
}
